import axios from 'axios';
import { supabase } from './supabase';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const api = axios.create({
  baseURL: API_URL,
  timeout: 15000, // 15s hard ceiling — no request should take longer
  headers: {
    'Content-Type': 'application/json',
  },
});

// Attach Supabase auth token to all requests.
// Token retrieval is bounded by a 2s timeout so a stalled Supabase
// token-refresh never wedges outbound requests.
api.interceptors.request.use(async (config) => {
  try {
    const sessionPromise = supabase.auth.getSession();
    const timeoutPromise = new Promise((resolve) =>
      setTimeout(() => resolve({ data: { session: null }, _timedOut: true }), 2000)
    );
    const result = await Promise.race([sessionPromise, timeoutPromise]);
    if (result?._timedOut) {
      console.warn('[api] supabase.auth.getSession() timed out after 2s — proceeding without token for', config.url);
    }
    const session = result?.data?.session;
    if (session?.access_token) {
      config.headers.Authorization = `Bearer ${session.access_token}`;
    }
  } catch (err) {
    console.warn('[api] getSession failed:', err?.message || err);
  }
  return config;
});

// Response logger — surfaces any hung/failed request in production DevTools.
// Also: a 401 usually means the request-side 2s getSession() race (above)
// lost and the request went out with no token at all — not that the user
// is actually unauthenticated. Wait properly for the real session once and
// retry. A 403 is left alone — that means a token WAS present and the
// server correctly rejected it, which retrying would just mask.
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const url = error?.config?.url;
    const status = error?.response?.status;
    const detail = error?.response?.data?.detail;

    if (status === 401 && error.config && !error.config._retriedAfterSessionWait) {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (session?.access_token) {
          error.config._retriedAfterSessionWait = true;
          error.config.headers = error.config.headers || {};
          error.config.headers.Authorization = `Bearer ${session.access_token}`;
          return api(error.config);
        }
      } catch { /* fall through to normal error handling below */ }
    }

    if (error?.code === 'ECONNABORTED') {
      console.error(`[api] TIMEOUT: ${url}`);
    } else if (status) {
      console.warn(`[api] ${status} ${url}${detail ? ` — ${detail}` : ''}`);
    } else {
      console.error(`[api] network error: ${url}`, error?.message);
    }
    return Promise.reject(error);
  }
);

// Auth
export const signup = (data) => api.post('/api/auth/signup', data);
export const login = (data) => api.post('/api/auth/login', data);
export const logout = () => api.post('/api/auth/logout');
export const forgotPassword = (email) => api.post(`/api/auth/forgot-password?email=${encodeURIComponent(email)}`);

// Users
export const getUser = (userId) => api.get(`/api/users/${userId}`);
export const updateUser = (userId, data) => api.put(`/api/users/${userId}`, data);
export const searchUsers = (query) => api.get(`/api/users/search/${query}`);
export const ensureProfile = (data) => api.post('/api/users/ensure-profile', data);
// Admin
export const getAllUsers = () => api.get('/api/admin/users');
export const deleteUser = (userId) => api.delete(`/api/admin/users/${userId}`);
export const makeAdmin = (data) => api.post('/api/admin/make-admin', data);
export const banUser = (data) => api.post('/api/admin/ban-user', data);
export const restrictPosting = (userId) => api.post(`/api/admin/users/${userId}/restrict-posting`);
export const unrestrictPosting = (userId) => api.post(`/api/admin/users/${userId}/unrestrict-posting`);
export const toggleMentor = (userId) => api.patch(`/api/admin/users/${userId}/toggle-mentor`);

// Groups / Spaces
export const getGroups = () => api.get('/api/groups');
export const getGroup = (groupId) => api.get(`/api/groups/${groupId}`);
export const uploadSpaceHeader = (groupId, formData) => api.post(`/api/groups/${groupId}/upload-header`, formData, {
  headers: { 'Content-Type': 'multipart/form-data' }
});

// Space Memberships
export const joinSpace = (spaceId, userId) => api.post(`/api/spaces/${spaceId}/join?user_id=${userId}`);
export const leaveSpace = (spaceId, userId) => api.post(`/api/spaces/${spaceId}/leave?user_id=${userId}`);
export const checkMembership = (spaceId, userId) => api.get(`/api/spaces/${spaceId}/membership?user_id=${userId}`);

// Space Modules & Lessons
export const getSpaceModules = (spaceId, userId, includeUnpublished) => api.get(`/api/spaces/${spaceId}/modules`, { params: { ...(userId ? { user_id: userId } : {}), ...(includeUnpublished ? { include_unpublished: true } : {}) } });
export const getLesson = (lessonId, userId) => api.get(`/api/lessons/${lessonId}`, { params: userId ? { user_id: userId } : {} });
export const completeLesson = (lessonId, userId) => api.post(`/api/lessons/${lessonId}/complete?user_id=${userId}`);
export const seedLmsContent = () => api.post('/api/spaces/seed-lms');

// Admin: Space/Module/Lesson CRUD
export const updateGroup = (groupId, data) => api.put(`/api/groups/${groupId}`, data);
export const createModule = (data) => api.post('/api/modules', data);
export const updateModule = (moduleId, data) => api.put(`/api/modules/${moduleId}`, data);
export const createLesson = (data) => api.post('/api/lessons', data);
export const updateLesson = (lessonId, data) => api.put(`/api/lessons/${lessonId}`, data);
export const uploadLessonMedia = (lessonId, formData) => api.post(`/api/lessons/${lessonId}/upload-media`, formData, {
  headers: { 'Content-Type': 'multipart/form-data' }
});
export const deleteModule = (moduleId) => api.delete(`/api/modules/${moduleId}`);
export const deleteLesson = (lessonId) => api.delete(`/api/lessons/${lessonId}`);
export const reorderModules = (items) => api.post('/api/modules/reorder', { items });
export const reorderLessons = (items) => api.post('/api/lessons/reorder', { items });
export const courseBuilderUpload = (formData) => api.post('/api/course-builder/upload', formData, {
  headers: { 'Content-Type': 'multipart/form-data' }
});

// Space Posts
export const getSpacePosts = (spaceId, params) => api.get(`/api/spaces/${spaceId}/posts`, { params });

// Avatars
export const getAvatars = (gender, userId) => api.get('/api/avatars', { params: { ...(gender ? { gender } : {}), ...(userId ? { user_id: userId } : {}) } });
export const addAvatar = (data) => api.post('/api/avatars', data);
export const uploadAvatar = (formData) => api.post('/api/avatars/upload', formData, {
  headers: { 'Content-Type': 'multipart/form-data' }
});
export const deleteAvatar = (avatarId) => api.delete(`/api/avatars/${avatarId}`);
export const seedAvatars = () => api.post('/api/avatars/seed');

// Posts
export const getPosts = (params) => api.get('/api/posts', { params });
export const createPost = (data, userId) => api.post(`/api/posts?user_id=${userId}`, data);
export const deletePost = (postId) => api.delete(`/api/posts/${postId}`);
export const editPost = (postId, content) => api.put(`/api/posts/${postId}`, { content });
export const repostPost = (postId, userId, comment = null) =>
  api.post(`/api/posts/${postId}/repost?user_id=${userId}`, { comment });
export const uploadPostImage = (formData) => api.post('/api/posts/upload-image', formData, {
  headers: { 'Content-Type': 'multipart/form-data' }, timeout: 180000,
});

// Islamojis
export const getIslamojis = () => api.get('/api/islamojis');
export const uploadIslamoji = (formData) => api.post('/api/admin/islamojis', formData, {
  headers: { 'Content-Type': 'multipart/form-data' }
});
export const deleteIslamoji = (id) => api.delete(`/api/admin/islamojis/${id}`);

// Likes
export const likePost = (postId, userId) => api.post(`/api/posts/${postId}/like?user_id=${userId}`);
export const checkLiked = (postId, userId) => api.get(`/api/posts/${postId}/liked?user_id=${userId}`);

// Comments
export const getComments = (postId, viewerId) => api.get(`/api/posts/${postId}/comments${viewerId ? `?viewer_id=${viewerId}` : ''}`);
export const createComment = (postId, data, userId) => api.post(`/api/posts/${postId}/comments?user_id=${userId}`, data);
export const deleteComment = (commentId) => api.delete(`/api/comments/${commentId}`);
export const likeComment = (commentId, userId) => api.post(`/api/comments/${commentId}/like?user_id=${userId}`);

// Pin / Polls
export const pinPost = (postId) => api.post(`/api/posts/${postId}/pin`);
export const pinPostTop = (postId) => api.post(`/api/posts/${postId}/pin-top`);
export const pinComment = (commentId) => api.post(`/api/comments/${commentId}/pin`);
export const voteOnPoll = (postId, optionIndex) => api.post(`/api/posts/${postId}/vote`, { option_index: optionIndex });

// Notifications
export const getNotifications = (userId) => api.get(`/api/notifications/${userId}`);
export const markNotificationRead = (notificationId) => api.put(`/api/notifications/${notificationId}/read`);
export const markAllNotificationsRead = (userId) => api.put(`/api/notifications/${userId}/read-all`);

// Search
export const search = (query) => api.get(`/api/search?query=${query}`);

// Seed data
export const seedData = () => api.post('/api/seed');

// Health check
export const healthCheck = () => api.get('/api/health');

// Dashboard
export const getDashboard = (userId) => api.get(`/api/dashboard/${userId}`);

// Onboarding
export const getOnboarding = (userId) => api.get(`/api/onboarding/${userId}`);
export const saveOnboarding = (userId, data) => api.post(`/api/onboarding/${userId}`, data);
export const updateOnboardingField = (userId, field, value) => api.put(`/api/onboarding/${userId}/field?field=${field}&value=${encodeURIComponent(value)}`);

// Financial Progress
export const logFinancialProgress = (userId, data) => api.post(`/api/financial-progress/${userId}`, data);
export const getFinancialProgress = (userId) => api.get(`/api/financial-progress/${userId}`);

// Salah Tracker
export const getSalahToday = (userId) => api.get(`/api/salah/${userId}/today`);
export const getSalahDate = (userId, date) => api.get(`/api/salah/${userId}/date/${date}`);
export const getSalahEarliest = (userId) => api.get(`/api/salah/${userId}/earliest`);
export const logSalah = (userId, data) => api.post(`/api/salah/${userId}/log`, data);
export const logSalahDate = (userId, date, data) => api.post(`/api/salah/${userId}/log/${date}`, data);
export const getSalahMonth = (userId, year, month) => api.get(`/api/salah/${userId}/month/${year}/${month}`);
export const getSalahStreak = (userId) => api.get(`/api/salah/${userId}/streak`);

// Dua of the Day
export const getDuaOfTheDay = () => api.get('/api/dua-of-the-day');

// Display name check
export const checkDisplayName = (name, currentUserId) => api.get(`/api/users/check-name/${encodeURIComponent(name)}`, { params: currentUserId ? { current_user_id: currentUserId } : {} });

// Badges
export const getAllBadges = () => api.get('/api/badges');
export const getUserBadges = (userId) => api.get(`/api/badges/user/${userId}`);
export const checkAndAwardBadges = (userId) => api.post(`/api/badges/check/${userId}`);
export const claimRamadanBadge = (userId) => api.post(`/api/badges/claim-ramadan/${userId}`);
export const claimShahadahBadge = (userId) => api.post(`/api/badges/claim-shahadah/${userId}`);
export const seedBadges = () => api.post('/api/badges/seed');

// Activity
export const logActivity = (userId, activityType, description) => api.post(`/api/activity/log?user_id=${userId}&activity_type=${encodeURIComponent(activityType)}&description=${encodeURIComponent(description)}`);

// Challenges
export const getTodayChallenge = () => api.get('/api/challenges/today');
export const acceptChallenge = (challengeId, userId) => api.post(`/api/challenges/${challengeId}/accept?user_id=${userId}`);
export const seedChallenges = () => api.post('/api/challenges/seed');

// Public Profiles
export const getPublicProfile = (username, viewerId) => api.get(`/api/profile/${encodeURIComponent(username)}`, { params: viewerId ? { viewer_id: viewerId } : {} });
export const canViewProfile = (username, viewerId) => api.get(`/api/profile/${encodeURIComponent(username)}/can-view`, { params: viewerId ? { viewer_id: viewerId } : {} });

// Follow
export const followUser = (targetId, userId) => api.post(`/api/follow/${targetId}?user_id=${userId}`);
export const unfollowUser = (targetId, userId) => api.delete(`/api/follow/${targetId}?user_id=${userId}`);

// Block
export const blockUser = (targetId, userId) => api.post(`/api/block/${targetId}?user_id=${userId}`);
export const unblockUser = (targetId, userId) => api.delete(`/api/block/${targetId}?user_id=${userId}`);
export const getBlockedUsers = (userId) => api.get(`/api/blocks/${userId}`);

// Wallpapers
export const getWallpapers = () => api.get('/api/wallpapers');
export const getUserWallpapers = (userId) => api.get(`/api/wallpapers/user/${userId}`);

// Profile Widgets
export const getProfileWidgets = (userId) => api.get(`/api/profile-widgets/${userId}`);
export const saveProfileWidgets = (userId, widgets) => api.put(`/api/profile-widgets/${userId}`, { widgets });

// Quran audio (Audio Player widget picker)
export const getQuranReciters = () => api.get('/api/quran-audio/reciters');
export const getQuranSurahsForReciter = (moshafId) => api.get(`/api/quran-audio/reciters/${moshafId}/surahs`);

// Single post permalink + Recent Posts widget
export const getPost = (postId, viewerId) => api.get(`/api/posts/${postId}`, { params: viewerId ? { viewer_id: viewerId } : {} });
export const getRecentPosts = (userId, limit = 5) => api.get(`/api/posts/recent/${userId}`, { params: { limit } });

// Pre-launch site gate (login/signup screen)
export const getSiteGateStatus = () => api.get('/api/site-gate/status');
export const unlockSiteGate = (password) => api.post('/api/site-gate/unlock', { password });

export const setActiveWallpaper = (userId, wallpaperId) => api.put(`/api/wallpapers/set/${userId}?wallpaper_id=${wallpaperId}`);
export const seedDefaultWallpaper = () => api.post('/api/wallpapers/seed-default');
export const adminGetWallpapers = () => api.get('/api/admin/wallpapers');
export const adminCreateWallpaper = (data) => api.post('/api/admin/wallpapers', data);
export const adminUpdateWallpaper = (id, data) => api.put(`/api/admin/wallpapers/${id}`, data);
export const adminDeleteWallpaper = (id) => api.delete(`/api/admin/wallpapers/${id}`);
export const adminUploadWallpaperImage = (formData) => api.post('/api/admin/wallpapers/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } });

// Stripe Premium
export const getStripeConfig = () => api.get('/api/stripe/config');
export const createStripeSubscription = (data) => api.post('/api/stripe/create-subscription', data);
export const relayMetaEvent = (data) => api.post('/api/meta/event', data);
export const finalizeLifetime = (paymentIntentId) => api.post('/api/stripe/finalize-lifetime', { payment_intent_id: paymentIntentId });
export const cancelStripeSubscription = (userId) => api.post('/api/stripe/cancel-subscription', { user_id: userId });
export const getSubscriptionStatus = (userId) => api.get(`/api/stripe/subscription-status/${userId}`);

// Ads (admin CRUD)
export const listAds = () => api.get('/api/admin/ads');
export const createAd = (data) => api.post('/api/admin/ads', data);
export const updateAd = (id, data) => api.put(`/api/admin/ads/${id}`, data);
export const deleteAd = (id) => api.delete(`/api/admin/ads/${id}`);
// Ads (public render)
export const serveAd = (placement, userId) => api.get(`/api/ads/serve`, { params: { placement, user_id: userId } });
export const logAdImpression = (adId, payload) => api.post(`/api/ads/${adId}/impression`, payload);
export const logAdClick = (adId, payload) => api.post(`/api/ads/${adId}/click`, payload);

// Direct Messaging
export const listConversations = (userId) => api.get(`/api/messages/conversations/${userId}`);
export const openConversation = (targetId) => api.post(`/api/messages/conversation/${targetId}`);
export const getMessages = (conversationId) => api.get(`/api/messages/conversation/${conversationId}/messages`);
export const sendMessage = (conversationId, content) => api.post(`/api/messages/conversation/${conversationId}/send`, { content });
export const deleteConversation = (conversationId) => api.delete(`/api/messages/conversation/${conversationId}`);
export const checkCanMessage = (viewerId, targetId) => api.get(`/api/messages/can-message/${viewerId}/${targetId}`);
export const searchMessageableUsers = (userId, q) => api.get(`/api/messages/users/search/${userId}`, { params: { q } });
export const unreadMessagesCount = (userId) => api.get(`/api/messages/unread-count/${userId}`);

// Reports + suspension + settings
export const reportPost = (postId, data) => api.post(`/api/posts/${postId}/report`, data);
export const listReports = (status = 'pending') => api.get(`/api/admin/reports`, { params: { status } });
export const dismissReport = (id) => api.put(`/api/admin/reports/${id}/dismiss`);
export const reportsDeletePost = (id) => api.put(`/api/admin/reports/${id}/delete-post`);
export const suspendUser = (userId, payload) => api.post(`/api/admin/users/${userId}/suspend`, payload);
export const unsuspendUser = (userId) => api.delete(`/api/admin/users/${userId}/suspend`);
export const getCrossGenderSetting = () => api.get('/api/settings/cross-gender-messaging');
export const setAdminSetting = (key, value) => api.put(`/api/admin/settings/${key}`, { value });
export const getMentorSignupSetting = () => api.get('/api/settings/mentor-signup');
export const getEventsSetting = () => api.get('/api/settings/events');

// Invite / referral
export const getInviteStats = (userId) => api.get(`/api/users/${userId}/invite-stats`);

// Delete Your Account
export const deleteAccount = (userId) => api.delete(`/api/users/${userId}/account`);

// Web Push subscriptions
export const savePushSubscription = (sub) => api.post('/api/push/subscribe', sub);
export const deletePushSubscription = (sub) => api.delete('/api/push/subscribe', { data: sub });

// Mentor Stripe add-on
export const createMentorSubscription = (data) => api.post('/api/stripe/create-mentor-subscription', data);
export const cancelMentorSubscription = (userId) => api.post('/api/stripe/cancel-mentor-subscription', { user_id: userId });

// Mentor discovery & profile
export const listMentors = () => api.get('/api/mentors');
export const updateMentorProfile = (data) => api.put('/api/mentor/profile', data);

// Mentor messaging (mentee side)
export const sendMentorMessage = (mentorId, content) => api.post(`/api/mentor-messages/send/${mentorId}`, { content });
export const getMentorThread = (mentorId) => api.get(`/api/mentor-messages/thread/${mentorId}`);
export const listMyMentors = () => api.get('/api/mentor-messages/my-mentors');

// Mentor Hub (mentor side)
export const mentorHubConversations = () => api.get('/api/mentor-hub/conversations');
export const mentorHubThread = (menteeId) => api.get(`/api/mentor-hub/thread/${menteeId}`);
export const mentorHubReply = (menteeId, content) => api.post(`/api/mentor-hub/reply/${menteeId}`, { content });
export const mentorHubDecline = (menteeId) => api.post(`/api/mentor-hub/decline/${menteeId}`);
export const mentorHubStats = () => api.get('/api/mentor-hub/stats');
export const getMentorPayouts = (month) => api.get('/api/admin/mentor-payouts', { params: month ? { month } : {} });
export const broadcastNotification = (message) => api.post('/api/admin/broadcast', { message });

// Seed Post Command Center
export const getSeedAiEnabled = () => api.get('/api/admin/seed-ai-enabled');
export const setSeedAiEnabled = (enabled) => api.post('/api/admin/seed-ai-enabled', { enabled });
export const getSeedQueue = () => api.get('/api/admin/seed-queue');
export const getSeedAuthors = () => api.get('/api/admin/seed-queue/authors');
export const bulkCreateSeedPosts = (posts) => api.post('/api/admin/seed-queue/bulk', { posts });
export const updateSeedPost = (id, data) => api.put(`/api/admin/seed-queue/${id}`, data);
export const deleteSeedPost = (id) => api.delete(`/api/admin/seed-queue/${id}`);

// Events
export const getEvents = (start, end) => api.get('/api/events', { params: { start, end } });
export const getEvent = (id) => api.get(`/api/events/${id}`);
export const getPublicEvent = (id) => api.get(`/api/events/public/${id}`);
export const registerForEvent = (id, data) => api.post(`/api/events/${id}/register`, data);
export const finalizeEventRegistration = (id, paymentIntentId) => api.post(`/api/events/${id}/finalize`, { payment_intent_id: paymentIntentId });
export const adminGetEvents = () => api.get('/api/admin/events');
export const adminCreateEvent = (data) => api.post('/api/admin/events', data);
export const adminUpdateEvent = (id, data) => api.put(`/api/admin/events/${id}`, data);
export const adminDeleteEvent = (id) => api.delete(`/api/admin/events/${id}`);
export const adminUploadEventImage = (id, formData) => api.post(`/api/admin/events/${id}/image`, formData, {
  headers: { 'Content-Type': 'multipart/form-data' }
});
export const adminGetEventRegistrations = (id) => api.get(`/api/admin/events/${id}/registrations`);
export const adminDownloadEventCsv = (id) => api.get(`/api/admin/events/${id}/registrations.csv`, { responseType: 'blob' });
export const adminMarkAttended = (regId, attended) => api.post(`/api/admin/events/registrations/${regId}/attended`, { attended });
export const adminKitCheck = () => api.get('/api/admin/events/kit-check');
export const adminSyncKit = (id) => api.post(`/api/admin/events/${id}/sync-kit`);
export const adminSetPhotoOptOut = (regId, optOut) => api.post(`/api/admin/events/registrations/${regId}/photo-opt-out`, { opt_out: optOut });
export const adminRefundRegistration = (regId) => api.post(`/api/admin/events/registrations/${regId}/refund`);

// Library
export const getLibrarySetting = () => api.get('/api/settings/library');
export const getLibrary = () => api.get('/api/library');
export const downloadLibraryItem = (id) => api.post(`/api/library/${id}/download`);
export const purchaseLibraryItem = (id, data) => api.post(`/api/library/${id}/purchase`, data);
export const finalizeLibraryPurchase = (id, paymentIntentId) => api.post(`/api/library/${id}/finalize`, { payment_intent_id: paymentIntentId });
export const adminGetLibraryPurchases = (id) => api.get(`/api/admin/library/${id}/purchases`);
export const adminRefundLibraryPurchase = (purchaseId, force = false) => api.post(`/api/admin/library/purchases/${purchaseId}/refund`, { force });
export const adminGetLibrary = () => api.get('/api/admin/library');
export const adminCreateLibraryItem = (data) => api.post('/api/admin/library', data);
export const adminUpdateLibraryItem = (id, data) => api.put(`/api/admin/library/${id}`, data);
export const adminDeleteLibraryItem = (id) => api.delete(`/api/admin/library/${id}`);
export const adminUploadLibraryFile = (id, formData, onUploadProgress) => api.post(`/api/admin/library/${id}/file`, formData, {
  headers: { 'Content-Type': 'multipart/form-data' }, onUploadProgress, timeout: 180000,
});
export const adminUploadLibraryCover = (id, formData) => api.post(`/api/admin/library/${id}/cover`, formData, {
  headers: { 'Content-Type': 'multipart/form-data' },
});
export const adminUploadLibraryBanner = (formData) => api.post('/api/admin/library-banner', formData, {
  headers: { 'Content-Type': 'multipart/form-data' },
});
export const adminUploadLibraryCard = (tier, formData) => api.post(`/api/admin/library-card/${tier}`, formData, {
  headers: { 'Content-Type': 'multipart/form-data' },
});
export const adminRemoveLibraryCard = (tier) => api.delete(`/api/admin/library-card/${tier}`);

export default api;

// The Wifey Application Builder (public): start the email confirmation, then ask whether it has been confirmed
export const wifeyRequest = (email) => api.post('/api/wifey/request', { email });
export const wifeyStatus = (email) => api.post('/api/wifey/status', { email });

// The Single Muslim Marriage Readiness Quiz (public): same email confirmation, plus the overall stage used for the Kit tag
export const quizRequest = ({ email, firstName, stage }) => api.post('/api/quiz/request', { email, first_name: firstName || '', stage });
export const quizStatus = (email) => api.post('/api/quiz/status', { email });

// Sold-out events: "tell me about future events" (visitors confirm by email; members are added straight away)
export const eventNotifyPublic = (email) => api.post('/api/events/notify-me', { email });
export const eventNotifyMember = () => api.post('/api/events/notify-me/member');
